import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DollarSign, Plus, Trash2, ShoppingBasket, Boxes } from 'lucide-react';
import Header from '../../components/Header';
import Tabs from '../../components/Tabs';
import Textarea from '../../components/Textarea';
import TabsCut from '../../components/TabsCut';
import Actions from '../../components/Actions';
import Button from '../../components/Button';
import List from '../../components/List';
import Line from '../../components/Line';
import Text from '../../components/Text';
import Title from '../../components/Title';
import { PRODUCTS_MAX_COUNT } from '../../utils/const';
import TabsList from '../../components/TabsList';
import TabsListItem from '../../components/TabsListItem';
import ConfirmationModal from '../../components/ConfirmationModal';
import NumberInput from '../../components/NumberInput';
import MobileMenuHeader from '../../components/MobileMenuHeader';
import TabsListTitle from '../../components/TabsListTitle';

const fields = ['name', 'unit', 'costPrice'];
const numberFields = ['costPrice'];

export default function VariableCost(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      common,
      tipCustomerSegment,
      caseStudies,
      costRevenue: {
        name,
        costPerUnit: { header, title, text, list, tabs },
      },
    },
  } = props;
  const { collection } = useSelector((state) => state.product);
  const {
    product: { addProduct, updateProduct, removeProduct },
  } = useDispatch();
  const [opened, setOpened] = useState(false);

  const getTableHeaderIconByIndex = (index) => {
    // eslint-disable-next-line default-case
    switch (index) {
      case 0:
        return <ShoppingBasket className="w-6 h-auto" />;
      case 1:
        return <Boxes className="w-6 h-auto" />;
      case 2:
        return <DollarSign className="w-6 h-auto" />;
    }
  };

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon="icon-cost"
        locales={locales}
        componentIndex={componentIndex}
        className="bmc-page-header--violet"
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="FinanceCanvas" title={name} color="violet" />
        }
      />

      <Text>{[text[0]]}</Text>

      <Title text={title[0]} align="left" />

      <List
        items={list}
        className="margin-top-40"
        icons={getTableHeaderIconByIndex}
      />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--violet">{title[1]}</span> {title[2]}
      </h2>

      <Text align="center">{[text[2]]}</Text>

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-60 bmc-page-tabs--violet"
      >
        {[
          <div key="strategy">
            <div>
              {collection.map((item, index) => (
                <TabsList
                  key={item.id}
                  divider={index < PRODUCTS_MAX_COUNT - 1}
                >
                  <TabsListTitle className="flex flex-row justify-between items-center">
                    {locales.cost.costPerUnitTitle} {index + 1}
                    {collection.length > 1 && (
                      <ConfirmationModal
                        className="ml-3 hover:bg-muted-lighter/60 text-muted"
                        body={locales.common.deleteConfirmation}
                        confirmText="Delete"
                        buttonAriaLabel="Delete"
                        onConfirm={() => removeProduct({ index, id: item.id })}
                      >
                        <Trash2 className="w-6 min-w-6 h-auto" />
                      </ConfirmationModal>
                    )}
                  </TabsListTitle>

                  {fields.map((field, i) => (
                    <TabsListItem
                      divider={false}
                      className="mb-5"
                      key={i}
                      icon={getTableHeaderIconByIndex(i)}
                      title={tabs[field]}
                      text={
                        // eslint-disable-next-line
                        <>
                          {numberFields.includes(field) ? (
                            <NumberInput
                              value={item[field]}
                              onChange={(event) =>
                                updateProduct({
                                  index,
                                  name: field,
                                  value: event.target.value,
                                  id: item.id,
                                })
                              }
                              placeholder={tabs[`${field}Placeholder`]}
                            />
                          ) : (
                            <Textarea
                              delay={700}
                              value={item[field] || ''}
                              onChange={(event) =>
                                updateProduct({
                                  index,
                                  name: field,
                                  value: event.target.value,
                                  id: item.id,
                                })
                              }
                              placeholder={tabs[`${field}Placeholder`]}
                            />
                          )}
                        </>
                      }
                    />
                  ))}
                </TabsList>
              ))}
            </div>

            {collection.length < PRODUCTS_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions">
                <Button
                  variant="outline"
                  onClick={addProduct}
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNewSomething}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  opened={opened}
                  setOpened={setOpened}
                  caseStudy={item}
                />

                <table className="w-full ">
                  <thead>
                    <tr>
                      {fields.map((field, i) => {
                        return (
                          <th className="w-1/3" key={field}>
                            <div className="flex flex-row items-center mb-6">
                              <span className="p-2.5 bg-muted-lighter/10 rounded-md flex items-center justify-center mr-3">
                                {getTableHeaderIconByIndex(i)}
                              </span>
                              <span className="text-xl">{tabs[field]}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {item.products.map((product, i) => (
                      <tr key={i}>
                        {fields.map((field) => (
                          <td key={field} className="text-start">
                            <div className="mb-4">{product[field]}</div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
